import parse, {
  domToReact,
  Element,
  HTMLReactParserOptions,
  attributesToProps,
} from "html-react-parser";
import React, { ReactNode } from "react";

const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "param",
  "source",
  "track",
  "wbr",
]);

const TABLE_TAGS = new Set(["table", "tbody", "thead", "tfoot", "tr"]);

/**
 * Default parse options
 */
const defaultParseOptions: HTMLReactParserOptions = {
  replace: (domNode) => {
    if (domNode instanceof Element) {
      const tagName = domNode.name ? domNode.name.toLowerCase() : "";

      // 1. Unwrap các thẻ namespace từ Word/Office (ví dụ: <o:p>, <w:sdt>)
      if (tagName.includes(":")) {
        return <>{domToReact(domNode.children as any, defaultParseOptions)}</>;
      }

      // 2. Unwrap nested tbody (ví dụ <tbody> bên trong <tbody>)
      if (
        tagName === "tbody" &&
        domNode.parent &&
        (domNode.parent as any).name &&
        (domNode.parent as any).name.toLowerCase() === "tbody"
      ) {
        return <>{domToReact(domNode.children as any, defaultParseOptions)}</>;
      }

      if (domNode.attribs) {
        const attribs = domNode.attribs;

        const cleanAttribs: Record<string, string> = {};
        for (const key in attribs) {
          // Bỏ qua các attribute namespace (như xmlns:o, o:preferrelative, v:shapes)
          if (key.includes(":") || !/^[a-zA-Z0-9\-_]+$/.test(key)) {
            continue;
          }
          cleanAttribs[key] = attribs[key];
        }

        // Chuyển đổi toàn bộ attribute thành React Props hợp lệ (style string -> style object, class -> className)
        const props = attributesToProps(cleanAttribs) as any;
        delete props.children;

        // Đảm bảo style không bao giờ là chuỗi (tránh React runtime error)
        if (typeof props.style === "string") {
          delete props.style;
        }

        // Xóa font-family và font-size inline để giao diện tuân theo component CSS (Montserrat)
        if (props.style && typeof props.style === "object") {
          if (props.style.fontFamily) {
            delete props.style.fontFamily;
          }
          if (props.style.fontSize) {
            delete props.style.fontSize;
          }
        }

        // 3. Void elements (br, img, hr, etc.) tuyệt đối không được có children
        if (VOID_ELEMENTS.has(tagName)) {
          return React.createElement(domNode.name, props);
        }

        // 4. Lọc bỏ các text nodes chỉ chứa khoảng trắng/xuống dòng trong thẻ bảng để tránh lỗi React Hydration mismatch
        let childrenToParse = domNode.children || [];
        if (TABLE_TAGS.has(tagName)) {
          childrenToParse = childrenToParse.filter((c: any) => {
            if (c.type === "text" && !c.data?.trim()) return false;
            return true;
          });
        }

        const children = domToReact(childrenToParse as any, defaultParseOptions);
        return React.createElement(domNode.name, props, children);
      }
    }
    return undefined;
  },
};

interface SanitizeDOMDataOptions extends HTMLReactParserOptions {}

/**
 * Sanitize + parse HTML string to ReactNode
 */
export default function sanitizeDOMData(
  htmlString: string,
  options: SanitizeDOMDataOptions = {}
): ReactNode {
  return parse(htmlString, {
    ...defaultParseOptions,
    ...options,
    trim: true,
  });
}
